import React, { Fragment, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'
import axios from 'axios'
import { UserContext, MetadataContext } from '../../../context'
import { AuthContext } from '../../../context/AuthProvider'
import PageHeader from '../../../components/PageHeader'
import { LaakariButton, ShadowContainer } from '../../../components/styled/uiComponents'
import FormButtonContainer from '../../../components/FormButtonContainer'
import FormSentConfirmation from '../FormSentConfirmation'
import FormStepper from '../../../components/FormStepper'
import { isSessionInvalidError } from '../../../utils/error'
import { buildApiUrl, parseFormData, universityList } from '../../../utils/utils'
import { putInviteHashLocalStorage } from '../../../utils/localStorage'
import {
  showStudentMembershipApplication,
  userFoundInAapeli,
  isStudent
} from '../../../utils/membershipChecks'
import Summary from './Summary'
import PersonalInfo from './PersonalInfo'
import { StudentApplicationFormSchema } from './schema'

const StudentApplicationForm = () => {
  const [page, setPage] = useState(0)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [submittedFormType, setSubmittedFormType] = useState<'STUDENTMEMBER' | 'STUDENT' | null>(
    null
  )

  const [student, setStudent] = useState(false)
  const [applyMembership, setApplyMembership] = useState(true)
  const [studyStart, setStudyStart] = useState('')

  const { user } = useContext(UserContext)
  const { metaData } = useContext(MetadataContext)
  const { bearerToken, handleLogout } = useContext(AuthContext)
  const navigate = useNavigate()
  const location = useLocation()

  // Track if the initial studentMember auto-jump has occurred
  const hasAutoJumped = useRef(false)
  // Track if we came from browser back navigation (to prevent auto-redirect to membershipApplication)
  const cameFromBackNavigation = useRef(false)

  // Sync page state with URL search params (for browser back/forward within the form)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stepFromUrl = parseInt(params.get('step') || '0', 10)
    // Mark as back navigation if URL changed to a previous step
    if (stepFromUrl < page) {
      cameFromBackNavigation.current = true
    }
    setPage(stepFromUrl)
    // If URL has a step, consider auto-jump as already handled
    if (stepFromUrl > 0) {
      hasAutoJumped.current = true
    }
  }, [location.search])

  const currentValidationSchema = StudentApplicationFormSchema[page]

  // Determine if cursus fields are editable: only when not coming fully from Aapeli data
  const aapeliCursusComplete = Boolean(
    user?.els_cursus?.els_oppilaitos?.els_koodiid &&
      user?.els_cursus?.els_lukukausi?.attributevalue &&
      user?.els_cursus?.els_aloitusvuosi
  )
  const watchedOppilaitos = undefined
  const isCursusEditable = !userFoundInAapeli(user) || !aapeliCursusComplete

  const methods = useForm<any>({
    shouldUnregister: false,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(currentValidationSchema, { context: { isCursusEditable } }),
    defaultValues: {
      formType: 'STUDENTMEMBER',
      membership_starts: 'WHEN_APPROVED',
      organization: {
        name: 'Suomen Lääkäriliitto'
      },
      els_hetu: user?.els_hetu,
      birthdate: user?.birthdate,
      lastname: user?.lastname,
      firstname: user?.firstname,
      nickname: user?.nickname,
      address: {
        els_postikieli: JSON.stringify({ attributevalue: 'fi', value: 'Suomi' })
      },
      cursus: {
        els_oppilaitos: '',
        els_aloitusvuosi: '',
        els_lukukausi: ''
      }
    }
  })

  const {
    register,
    control,
    getValues,
    setValue,
    setFocus,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger
  } = methods

  const handleNext = async (e: any) => {
    e.preventDefault()
    const isStepValid = await trigger()
    if (isStepValid) {
      const nextPage = page + 1
      navigate(`/studentApplication?step=${nextPage}`)
    } else {
      // Debug invalid state to identify which field blocks progression
      // eslint-disable-next-line no-console
      console.log('StudentApplication step validation errors:', errors)
      const findFirstErrorPath = (errObj: any, prefix = ''): string | null => {
        for (const key of Object.keys(errObj)) {
          const value = errObj[key]
          const path = prefix ? `${prefix}.${key}` : key
          if (value?.types || value?.message) {
            return path
          }
          if (typeof value === 'object') {
            const childPath = findFirstErrorPath(value, path)
            if (childPath) {
              return childPath
            }
          }
        }
        return null
      }
      const firstError = findFirstErrorPath(errors)
      if (firstError) {
        try {
          setFocus(firstError as any)
        } catch (_err) {
          // ignore focus errors
        }
      }
      toast.error(`Tarkista punaisella merkityt kentät: ${firstError || ''}`)
    }

    // set these separately as they are not part of the form values for some reason
    setValue('lastname', user?.lastname)
    setValue('firstname', user?.firstname)
    setValue('els_hetu', user?.els_hetu)
    setValue('birthdate', user?.birthdate)
  }

  // Pre-fill required cursus fields for Aapeli users so validation passes
  useEffect(() => {
    if (page >= 2 && student && userFoundInAapeli(user)) {
      const oppilaitos = user?.els_cursus?.els_oppilaitos
      const lukukausi = user?.els_cursus?.els_lukukausi
      const aloitusvuosi = user?.els_cursus?.els_aloitusvuosi
      const postikieli = user?.postikieli
      const country = user?.address1_country

      if (oppilaitos) {
        setValue(
          'cursus.els_oppilaitos',
          JSON.stringify({
            els_koodiid: oppilaitos.els_koodiid,
            els_koodinnimi: oppilaitos.els_koodinnimi
          })
        )
      }
      if (lukukausi) {
        setValue(
          'cursus.els_lukukausi',
          JSON.stringify({ attributevalue: lukukausi.attributevalue, value: lukukausi.value })
        )
      }
      if (aloitusvuosi) {
        setValue('cursus.els_aloitusvuosi', `${aloitusvuosi}`)
      }

      if (postikieli?.attributevalue && postikieli?.value) {
        setValue(
          'address.els_postikieli',
          JSON.stringify({ attributevalue: postikieli.attributevalue, value: postikieli.value })
        )
      } else {
        // Default to Finnish when missing
        setValue('address.els_postikieli', JSON.stringify({ attributevalue: 'fi', value: 'Suomi' }))
      }

      if (country?.els_koodiid && country?.els_koodinnimi) {
        setValue(
          'address.country',
          JSON.stringify({
            els_koodiid: country.els_koodiid,
            els_koodinnimi: country.els_koodinnimi
          })
        )
      } else {
        // Default to Finland (Suomi) when country is missing
        setValue(
          'address.country',
          JSON.stringify({
            els_koodiid: 'd42381f6-f72b-ec11-b6e5-000d3a25c645',
            els_koodinnimi: 'Suomi'
          })
        )
      }

      // Pre-fill other required personal fields to ensure validation syncs with view
      setValue('nickname', user?.nickname || '')
      setValue('address.line1', user?.address1_line1 || '')
      setValue('address.line2', user?.address1_line2 || '')
      setValue('address.line3', user?.address1_line3 || '')
      setValue('address.postalcode', user?.address1_postalcode || '')
      setValue('address.city', user?.address1_city || '')
      setValue('mobilephone', user?.mobilephone || '')
      setValue('emailaddress1', user?.emailaddress1 || '')
    }
  }, [page, student, user, setValue])

  // For non-Aapeli users: prefill cursus fields from study_start (step 2)
  useEffect(() => {
    if (page >= 2 && student && !userFoundInAapeli(user) && studyStart) {
      try {
        const chosenDate = new Date(studyStart)
        if (!isNaN(chosenDate.getTime())) {
          const year = chosenDate.getFullYear()
          const juneFirst = new Date(year, 5, 1) // months are 0-indexed
          const semesterName = chosenDate < juneFirst ? 'Kevät' : 'Syksy'

          setValue('cursus.els_aloitusvuosi', `${year}`)

          const semester = metaData?.lukukaudet?.find(
            (opt: any) => opt?.value?.toLowerCase?.() === semesterName.toLowerCase()
          )

          if (semester?.attributevalue && semester?.value) {
            setValue(
              'cursus.els_lukukausi',
              JSON.stringify({ attributevalue: semester.attributevalue, value: semester.value })
            )
          } else {
            setValue('cursus.els_lukukausi', '')
          }
        }
      } catch (_err) {
        // ignore invalid date parsing
      }
    }
  }, [page, student, studyStart, metaData, user, setValue])

  const handleBack = useCallback(
    (e: any) => {
      e.preventDefault()
      if (page > 0) {
        const prevPage = page - 1
        const newUrl =
          prevPage === 0 ? '/studentApplication' : `/studentApplication?step=${prevPage}`
        navigate(newUrl)
      } else {
        // On first page, go back to home/login
        navigate('/')
      }
    },
    [page, navigate]
  )

  // Prefill cursus for students from sidosryhmat when entering the details step or summary
  useEffect(() => {
    if (page >= 2 && student && user && Array.isArray(user.sidosryhmat)) {
      const now = new Date()
      const activeStudentGroup = user.sidosryhmat.find((sr: any) => {
        const isStudentGroup = sr['paataso.els_paataso'] === 'Lääketieteen opiskelijat'
        const end = sr['els_paattymispaiva'] ? new Date(sr['els_paattymispaiva']) : null
        return isStudentGroup && (!end || end > now)
      })

      if (activeStudentGroup) {
        // Only populate if cursus fields are empty
        const currentOppilaitos = getValues('cursus.els_oppilaitos')
        const currentLukukausi = getValues('cursus.els_lukukausi')
        const currentAloitusvuosi = getValues('cursus.els_aloitusvuosi')

        const alkupaiva: string | undefined = activeStudentGroup['els_alkupaiva']
        if (alkupaiva && (!currentAloitusvuosi || currentAloitusvuosi === '')) {
          const d = new Date(alkupaiva)
          const year = d.getFullYear()
          const juneFirst = new Date(year, 5, 1)
          const semesterName = d < juneFirst ? 'Kevät' : 'Syksy'

          setValue('cursus.els_aloitusvuosi', `${year}`)

          if (metaData?.lukukaudet && (!currentLukukausi || currentLukukausi === '')) {
            const semester = metaData.lukukaudet.find(
              (opt: any) => opt?.value?.toLowerCase?.() === semesterName.toLowerCase()
            )
            if (semester?.attributevalue && semester?.value) {
              setValue(
                'cursus.els_lukukausi',
                JSON.stringify({ attributevalue: semester.attributevalue, value: semester.value })
              )
            }
          }
        }

        const tarkenne: string | undefined = activeStudentGroup['tarkenne.els_sidosryhmantarkenne']
        if (tarkenne && (!currentOppilaitos || currentOppilaitos === '')) {
          const parts = tarkenne.split(' - ')
          const schoolName = parts[parts.length - 1]?.trim()
          if (schoolName) {
            const uni = universityList.find(
              u => u.els_koodinnimi?.toLowerCase?.() === schoolName.toLowerCase()
            )
            if (uni) {
              setValue(
                'cursus.els_oppilaitos',
                JSON.stringify({ els_koodiid: uni.els_koodiid, els_koodinnimi: uni.els_koodinnimi })
              )
            }
          }
        }
      }
    }
  }, [page, student, user, metaData, setValue, getValues])
  const onSubmit = async (data: any) => {
    // remove items from data that would cause validation errors
    delete data.study_start
    //delete data.membership_starts

    // Ensure postal language is properly formatted
    if (data?.address?.els_postikieli) {
      try {
        // Try to parse the value - it should be a JSON string
        const parsedPostikieli = JSON.parse(data.address.els_postikieli)
        // Ensure attributevalue is set correctly (API might expect number 358 for Finnish)
        if (parsedPostikieli && typeof parsedPostikieli === 'object') {
          parsedPostikieli.attributevalue = 358
          data.address.els_postikieli = JSON.stringify(parsedPostikieli)
        }
      } catch (parseError) {
        // If parsing fails, log the error but don't break the submission
        // The value might already be in the correct format or might need to be set from metadata
        console.warn(
          'Failed to parse postal language value:',
          data.address.els_postikieli,
          parseError
        )
        // Try to set a default value if parsing fails
        const defaultPostikieli = metaData?.postikielet?.find((lang: any) => lang.value === 'Suomi')
        if (defaultPostikieli) {
          data.address.els_postikieli = JSON.stringify({
            attributevalue: 358,
            value: defaultPostikieli.value
          })
        }
      }
    }

    try {
      // Use same endpoint as StudentForm; differentiate by formType
      const formType = applyMembership ? 'STUDENTMEMBER' : 'STUDENT'
      data.formType = formType
      if (data.formType === 'STUDENT') {
        delete data.membership_starts
      }
      // Store the form type for the confirmation page title
      setSubmittedFormType(formType)
      const parsedData = parseFormData(data)
      const url = buildApiUrl('membership-application')
      await axios.post(url, parsedData, {
        headers: { 'x-tietoni-token': `Bearer ${bearerToken}` }
      })
      setFormSubmitted(true)
    } catch (error) {
      toast.error('Virhe lomakkeen lähetyksessä. Yritä uudelleen.')
      console.log('error when submitting form', error)
      if (isSessionInvalidError(error)) {
        toast.error('Istunto on päättynyt. Kirjaudu uudelleen sisään.')
        handleLogout(() => navigate('/'))
      }
    }
  }

  const handleStudentFormClick = () => {
    // generate a random hash to access student form
    const randomHash =
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    putInviteHashLocalStorage(randomHash)
    navigate('/studentForm')
  }

  // if "doctor" is selected on page 1, redirect to membership application
  useEffect(() => {
    // Don't auto-redirect if we came from browser back navigation
    if (cameFromBackNavigation.current) {
      cameFromBackNavigation.current = false
      return
    }
    if (page === 1 && !student) {
      // Use replace: true so browser back goes to step 0, not step 1
      // (which would trigger this redirect again)
      navigate('/membershipApplication', { replace: true })
    }
  }, [page, student, navigate])

  const pageProps = { register, user, errors, metaData, getValues, control }

  const studentMember = showStudentMembershipApplication(user)

  let canApply = false

  if (studentMember) {
    canApply = true
  } else {
    // user can apply for a student membership if they started their studies more than 3 years and 4 months ago
    const threeYearsAndFourMonthsAgo = new Date()
    threeYearsAndFourMonthsAgo.setMonth(threeYearsAndFourMonthsAgo.getMonth() - 40)

    canApply = new Date(studyStart).getTime() <= threeYearsAndFourMonthsAgo.getTime()
  }

  useEffect(() => {
    // Only auto-jump once on initial load, not on every render
    // This allows users to navigate back without being forced to page 3
    if (studentMember && !hasAutoJumped.current) {
      hasAutoJumped.current = true
      // Jump straight to summary for eligible Aapeli students
      setStudent(true)
      setApplyMembership(true)
      navigate('/studentApplication?step=3', { replace: true })
    }
  }, [studentMember, navigate])

  // Ensure default selection based on eligibility on step 2 (date step)
  useEffect(() => {
    if (page === 1 && student) {
      setApplyMembership(Boolean(canApply))
    }
  }, [page, student, canApply])

  const eligibleAapeliStudent = userFoundInAapeli(user) && studentMember

  // Determine the form title for confirmation page based on what was actually submitted
  const getFormTitle = () => {
    // Use the stored form type from submission, or fall back to current applyMembership state
    const formType = submittedFormType || (applyMembership ? 'STUDENTMEMBER' : 'STUDENT')
    return formType === 'STUDENTMEMBER'
      ? 'Liity opiskelijajäseneksi'
      : 'Ilmoita tietosi Lääkäriliittoon'
  }

  return metaData && user ? (
    <Fragment>
      {formSubmitted ? (
        <FormSentConfirmation formName={getFormTitle()} />
      ) : (
        <Fragment>
          <PageHeader />
          <div style={{ padding: '3vh' }}>
            {page > 1 && (
              <Typography variant='h3' color='primary' sx={{ paddingBottom: '3vh' }}>
                {!canApply || (!userFoundInAapeli(user) && !applyMembership)
                  ? 'Ilmoita tietosi Lääkäriliittoon'
                  : 'Liity opiskelijajäseneksi'}
              </Typography>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <FormStepper
                  activeStep={eligibleAapeliStudent ? 0 : page}
                  stepsCount={eligibleAapeliStudent ? 0 : 4}
                />
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <ShadowContainer>
                      {!eligibleAapeliStudent && page === 0 && (
                        <>
                          <Typography variant='h4' color='primary'>
                            Oletko lääkäri vai opiskelija?
                          </Typography>
                          <p>
                            Jos olet valmistunut lääkäriksi ETA-alueen ulkopuolelta etkä saanut
                            vielä Valviran laillistusta, olethan yhteydessä{' '}
                            <a href='mailto:laakariliitto@laakariliitto.fi'>
                              laakariliitto@laakariliitto.fi
                            </a>
                            .
                          </p>
                          <Grid container spacing={3} direction={{ xs: 'column', md: 'row' }}>
                            <Grid item xs={12}>
                              <FormControl>
                                <FormLabel>Valitse jäsentyyppi</FormLabel>
                                <RadioGroup
                                  row
                                  value={student ? 'student' : 'doctor'}
                                  onChange={e => setStudent(e.target.value === 'student')}>
                                  <FormControlLabel
                                    value='doctor'
                                    control={<Radio />}
                                    label='Lääkäri'
                                  />
                                  <FormControlLabel
                                    value='student'
                                    control={<Radio />}
                                    label='Opiskelija'
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </>
                      )}

                      {!eligibleAapeliStudent && page === 1 && student && (
                        <>
                          <Typography variant='h4' color='primary'>
                            Milloin opinnot ovat alkaneet?
                          </Typography>
                          <Grid container spacing={3} direction={{ xs: 'column', md: 'row' }}>
                            <Grid item xs={12}>
                              <TextField
                                {...register('study_start', { required: 'Pakollinen kenttä' })}
                                label='Opintojen aloitus'
                                type='date'
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={studyStart || ''}
                                onChange={e => setStudyStart(e.target.value)}
                                required
                                error={errors.study_start ? true : false}
                                helperText='Valitse päivämäärä kalenterista'
                                placeholder=''
                                sx={{
                                  marginBlockStart: '1em',
                                  marginBlockEnd: '1em',
                                  // Hide Safari's default date placeholder when field is empty
                                  '& input[type="date"]:not([value])::-webkit-datetime-edit': {
                                    opacity: 0
                                  },
                                  '& input[type="date"]:not([value])::-webkit-datetime-edit-fields-wrapper':
                                    {
                                      opacity: 0
                                    },
                                  '& input[type="date"]:not([value])::-webkit-inner-spin-button': {
                                    opacity: 0
                                  },
                                  '& input[type="date"]:not([value])::-webkit-calendar-picker-indicator':
                                    {
                                      opacity: 0.5
                                    },
                                  // Show content when value is set
                                  '& input[type="date"][value]::-webkit-datetime-edit': {
                                    opacity: 1
                                  }
                                }}
                              />
                            </Grid>
                            {canApply && !userFoundInAapeli(user) && (
                              <Grid item xs={12}>
                                <Typography
                                  variant='h5'
                                  color='primary'
                                  sx={{ paddingBottom: '1vh' }}>
                                  Valitse, mitä haluat tehdä
                                </Typography>
                                <FormControl>
                                  <FormLabel>Vaihtoehto</FormLabel>
                                  <RadioGroup
                                    value={applyMembership ? 'apply' : 'info'}
                                    onChange={e => setApplyMembership(e.target.value === 'apply')}
                                    row>
                                    <FormControlLabel
                                      value='apply'
                                      control={<Radio />}
                                      label='Haen Lääkäriliiton opiskelijajäsenyyttä'
                                    />
                                    <FormControlLabel
                                      value='info'
                                      control={<Radio />}
                                      label='En hae vielä Lääkäriliiton jäsenyyttä, ilmoitan vain tietoni.'
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                            )}
                          </Grid>
                        </>
                      )}

                      {(eligibleAapeliStudent ? page === 2 : page === 2) && student && (
                        <>
                          {!canApply && (
                            <>
                              <p>
                                Voit hakea opiskelijajäsenyyttä Lääkäriliitossa vasta kun opintojesi
                                alusta on kulunut vähintään 3 vuotta 4 kuukautta.
                              </p>
                              <p>
                                Ilmoita kuitenkin tietosi jo nyt – saat opiskelujen alusta alkaen
                                monia hyödyllisiä etuja käyttöösi.
                              </p>
                            </>
                          )}
                          <PersonalInfo watch={watch} {...pageProps} />
                        </>
                      )}

                      {(eligibleAapeliStudent ? page === 3 : page === 3) && (
                        <>
                          <Summary formState={watch()} user={user} metaData={metaData} />
                        </>
                      )}
                    </ShadowContainer>

                    <Grid
                      container
                      spacing={3}
                      direction='row'
                      wrap='wrap-reverse'
                      alignItems='center'
                      justifyContent='space-between'
                      sx={{ paddingTop: '3vh' }}>
                      {page === 0 && userFoundInAapeli(user) && (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='left'>
                            <LaakariButton
                              variant='outlined'
                              onClick={() => navigate('/#membership')}>
                              Palaa etusivulle
                            </LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      )}

                      {(!eligibleAapeliStudent && page !== 0) ||
                      (eligibleAapeliStudent && page === 2) ? (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='left'>
                            <LaakariButton
                              variant='outlined'
                              disabled={!eligibleAapeliStudent ? page === 0 : false}
                              onClick={eligibleAapeliStudent ? () => navigate('/') : handleBack}>
                              Takaisin
                            </LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      ) : null}
                      {!eligibleAapeliStudent && page !== 3 ? (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='right'>
                            <LaakariButton onClick={handleNext}>Jatka</LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      ) : (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='right'>
                            <LaakariButton disabled={isSubmitting} type='submit'>
                              Lähetä
                            </LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      )}
                      {eligibleAapeliStudent && page === 3 && (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='left'>
                            <LaakariButton
                              variant='outlined'
                              onClick={() => navigate('/updateInfo')}>
                              Muokkaa tietoja
                            </LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      )}
                    </Grid>
                  </form>
                </FormProvider>
              </Grid>
            </Grid>
          </div>
        </Fragment>
      )}
    </Fragment>
  ) : (
    <LinearProgress />
  )
}

export default StudentApplicationForm
